import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { usePlatform } from '../../lib/platform'
import { Shell } from '../../components/Shell'
import PortalFrame from '../../components/PortalFrame'
import { PcoIcon, Sill, Spinner, ToggleRow, Field, Check } from '../../components/ui'
import { GST, TRIAL_DAYS, money, quoteFor } from '../../lib/pricing'
import { report } from '../../lib/report'

/** The four church-level toggles, with the copy the setup screen uses. */
const DEFAULTS = [
  ['require_senior_signoff', 'Require senior sign-off', 'A pastor must approve before scheduling.'],
  ['show_writers_full_trail', 'Show writers the full trail', 'Writers can see every step their song has moved through.'],
  ['allow_voice_memo_only', 'Allow voice-memo-only submissions', 'Accept rough demos, not just finished recordings.'],
  ['two_sunday_test', 'Two-Sunday test', 'Test a song twice before calling it a yes.'],
]

/**
 * Common middle steps, offered as one-click add-backs rather than something
 * an admin has to retype from memory. Includes both the current default seed
 * (create_church, migration 20260822) and the process it replaced — deleting
 * a step here should never mean it's gone for good, just that it's back to
 * being a suggestion instead of already in the list.
 */
const SUGGESTED_STEPS = [
  ['Initial Pastor Listen', null],
  ['Request Cover from Another Team member', null],
  ['Band run through', null],
  ['Worship pastor review', 'Ticks, listen, decision'],
  ['Pair or rework loop', 'Optional development pass'],
  ['Senior pastor sign-off', 'Theology & culture fit'],
  ['Two-Sunday congregation test', 'Does the room sing it'],
]

/**
 * Congregation size, and what the plan would cost.
 *
 * Sizing lives on the church rather than on a subscription because it is true
 * about the church whether or not they ever pay — and asking for it once, here,
 * is far kinder than backfilling it across every church later.
 *
 * The quote is shown, not charged. Nothing here takes money; a church settles an
 * annual invoice by bank transfer the same way they already settle CCLI.
 */
function ChurchSize({ church, onSaved }) {
  const [f, setF] = useState({
    weekly_attendance: church.weekly_attendance ?? '',
    worship_team_size: church.worship_team_size ?? '',
    is_multisite: church.is_multisite ?? false,
    campus_count: church.campus_count ?? 1,
  })
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState(null)

  const num = (v) => (v === '' || v === null ? null : Number(v))
  const set = (k, v) => { setSaved(false); setF({ ...f, [k]: v }) }

  const save = async () => {
    setBusy(true); setErr(null)
    const patch = {
      weekly_attendance: num(f.weekly_attendance),
      worship_team_size: num(f.worship_team_size),
      is_multisite: f.is_multisite,
      campus_count: f.is_multisite ? Math.max(Number(f.campus_count) || 1, 1) : 1,
    }
    const { error } = await supabase.from('churches').update(patch).eq('id', church.id)
    setBusy(false)
    if (error) return setErr(readableError(error))
    setSaved(true)
    onSaved({ ...church, ...patch })
  }

  const quote = quoteFor({ ...church, ...{
    weekly_attendance: num(f.weekly_attendance),
    campus_count: f.is_multisite ? Number(f.campus_count) || 1 : 1,
  } })

  return (
    <div className="pf-card" style={{ marginTop: 14 }}>
      <div className="pf-cardhead"><span className="pf-cardtitle">Your church</span></div>
      <p className="pf-empty" style={{ marginBottom: 12 }}>
        Sets your plan band, the same way a CCLI licence is sized. Nothing here charges you.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
        <Field label="Average weekly attendance">
          <input className="input" type="number" min="0" inputMode="numeric"
                 value={f.weekly_attendance} placeholder="e.g. 250"
                 onChange={(e) => set('weekly_attendance', e.target.value)} />
        </Field>
        <Field label="People on the worship team">
          <input className="input" type="number" min="0" inputMode="numeric"
                 value={f.worship_team_size} placeholder="e.g. 12"
                 onChange={(e) => set('worship_team_size', e.target.value)} />
        </Field>
      </div>

      <ToggleRow title="More than one campus"
                 hint="Each site gets its own team and administrators"
                 on={f.is_multisite} onChange={(v) => set('is_multisite', v)} />
      {f.is_multisite && (
        <Field label="Campuses, including the main one">
          <input className="input" type="number" min="1" inputMode="numeric"
                 value={f.campus_count}
                 onChange={(e) => set('campus_count', e.target.value)} />
        </Field>
      )}

      <div style={{ borderTop: '1px solid var(--line)', marginTop: 12, paddingTop: 12 }}>
        {quote.ready ? (
          <>
            {quote.lines.map((l) => (
              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                <span>{l.label}<br /><span className="pf-empty">{l.detail}</span></span>
                <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(l.cents)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontWeight: 600 }}>
              <span>Per year{GST.registered ? ' (incl. GST)' : ''}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(quote.totalCents)}</span>
            </div>
            <p className="pf-empty" style={{ marginTop: 8 }}>
              First {TRIAL_DAYS} days free. Invoiced annually — no card, no direct debit.
            </p>
          </>
        ) : (
          <p className="pf-empty">{quote.reason} Add it above to see your plan.</p>
        )}
      </div>

      <button className="pf-btn pf-btn-solid" style={{ marginTop: 12 }} disabled={busy || saved} onClick={save}>
        {saved ? 'Saved' : busy ? 'Saving…' : 'Save church details'}
      </button>
      {err && <p className="err">{err}</p>}
    </div>
  )
}

export default function Process() {
  const { church, refresh } = useSession()
  const { device } = usePlatform()
  const [steps, setSteps] = useState(null)
  const [c, setC] = useState(null)
  const [err, setErr] = useState(null)
  const [saved, setSaved] = useState(false)
  const [draft, setDraft] = useState(null) // { label, description }
  const [pcoConnected, setPcoConnected] = useState(null) // null = loading
  const [pcoBusy, setPcoBusy] = useState(false)
  const [pcoNotice, setPcoNotice] = useState(null)
  // Whether the popup already reported a result. A ref, not state, because
  // the close-poll below has to read the current value from inside an
  // interval closure that was created before the message arrived.
  const pcoSettled = useRef(false)
  const dragFrom = useRef(null)

  const load = useCallback(async () => {
    if (!church) return
    const { data, error } = await supabase.from('church_review_steps')
      .select('*').eq('church_id', church.id).eq('active', true).order('position')
    if (error) setErr(readableError(error))
    setSteps(data ?? [])
    setC({ ...church })
  }, [church])

  useEffect(() => { load() }, [load])

  // Reads whether this church already has a Planning Center connection, and
  // separately whether we just landed back from pco-oauth-callback's redirect
  // (?pco=connected|error|expired) — the query param is a one-time notice,
  // cleared from the URL immediately so refreshing the page doesn't repeat it.
  useEffect(() => {
    if (!church) return
    supabase.rpc('church_pco_connected', { p_church_id: church.id })
      .then(({ data }) => setPcoConnected(!!data))

    const params = new URLSearchParams(window.location.search)
    const pco = params.get('pco')
    if (pco) {
      setPcoNotice(pco)
      if (pco === 'connected') setPcoConnected(true)
      const url = new URL(window.location.href)
      url.searchParams.delete('pco')
      window.history.replaceState({}, '', url)
    }
  }, [church])

  // Planning Center hands its result back through the popup, which relays it
  // with postMessage (see main.jsx) and closes itself. Origin-checked, because
  // any page can postMessage at us.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return
      if (e.data?.source !== 'beseter-pco') return
      pcoSettled.current = true
      setPcoBusy(false)
      setPcoNotice(e.data.status)
      if (e.data.status === 'connected') setPcoConnected(true)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const connectPlanningCenter = async () => {
    setPcoBusy(true)
    setErr(null)
    setPcoNotice(null)
    pcoSettled.current = false

    // Opened before the await, not after. A popup only counts as user-initiated
    // if window.open runs inside the click's own task — open it after an
    // awaited fetch and every browser blocks it. So it opens blank and is
    // pointed at the authorize URL once we have one.
    const w = 560, h = 720
    // dualScreen* for a second monitor: screenX is negative or offset there, so
    // centring on innerWidth alone puts the popup on the wrong display.
    const dx = window.screenLeft ?? window.screenX ?? 0
    const dy = window.screenTop ?? window.screenY ?? 0
    const vw = window.outerWidth || document.documentElement.clientWidth
    const vh = window.outerHeight || document.documentElement.clientHeight
    const features = `width=${w},height=${h},left=${Math.round(dx + (vw - w) / 2)},` +
      `top=${Math.round(dy + (vh - h) / 2)},resizable,scrollbars,status`
    const popup = window.open('', 'beseter-pco', features)

    const { data, error } = await supabase.functions
      .invoke('pco-oauth-start', { body: { church_id: church.id } })

    if (error) {
      popup?.close()
      setPcoBusy(false)
      report(error, 'planning-center', { step: 'oauth-start', church_id: church.id })
      return setErr(readableError(error))
    }
    if (!data?.url) {
      popup?.close()
      setPcoBusy(false)
      // No error and no URL means the function returned a shape we don't
      // understand — worth a report precisely because nothing threw.
      report(new Error('pco-oauth-start returned no url'), 'planning-center', {
        step: 'oauth-start', church_id: church.id,
      })
      return setErr('Could not start the Planning Center connection.')
    }

    // Blocked popup — the redirect flow still works, so use it rather than
    // telling an admin to go change a browser setting.
    if (!popup || popup.closed) {
      window.location.href = data.url
      return
    }
    popup.location.href = data.url

    // Cancelling authorisation means closing the window, which fires no message
    // at all. Poll for the close so the button doesn't sit on "Connecting…"
    // forever. A success closes the popup too, but the message has already
    // landed and cleared pcoBusy by then, so this only ever catches the cancel.
    const timer = setInterval(() => {
      if (!popup.closed) return
      clearInterval(timer)
      if (pcoSettled.current) return
      setPcoBusy(false)
      setPcoNotice('cancelled')
    }, 500)
  }

  const disconnectPlanningCenter = async () => {
    if (!window.confirm(
      'Disconnect Planning Center? Endorsed songs can no longer be pushed to your '
      + 'Services library until you reconnect. Nothing already pushed is removed.'
    )) return
    setPcoBusy(true)
    setErr(null)
    setPcoNotice(null)
    const { error } = await supabase.rpc('church_pco_disconnect', { p_church_id: church.id })
    setPcoBusy(false)
    if (error) {
      report(error, 'planning-center', { step: 'disconnect', church_id: church.id })
      return setErr(readableError(error))
    }
    setPcoConnected(false)
  }

  /** Rewrite every position in one pass so the order is always contiguous. */
  const persistOrder = async (list) => {
    setSteps(list)
    const { error } = await supabase.from('church_review_steps').upsert(
      list.map((s, i) => ({ ...s, position: i + 1 }))
    )
    if (error) setErr(readableError(error))
  }

  const move = (from, to) => {
    if (to < 0 || to >= steps.length) return
    if (steps[from].is_fixed || steps[to].is_fixed) return
    const list = [...steps]
    const [x] = list.splice(from, 1)
    list.splice(to, 0, x)
    persistOrder(list)
  }

  const addStep = async (label, description) => {
    const lastIdx = steps.length - 1 // keep the fixed "Into rotation" last
    const { data, error } = await supabase.from('church_review_steps')
      .insert({ church_id: church.id, label: label.trim(),
                description: description?.trim() || null, position: lastIdx + 1 })
      .select().single()
    if (error) return setErr(readableError(error))
    const list = [...steps]
    list.splice(lastIdx, 0, data)
    setDraft(null)
    persistOrder(list)
  }

  const removeStep = async (s) => {
    const { error } = await supabase.from('church_review_steps').delete().eq('id', s.id)
    if (error) return setErr(readableError(error))
    persistOrder(steps.filter((x) => x.id !== s.id))
  }

  const saveChurch = async () => {
    setErr(null)
    const { error } = await supabase.from('churches').update({
      require_senior_signoff: c.require_senior_signoff,
      show_writers_full_trail: c.show_writers_full_trail,
      allow_voice_memo_only: c.allow_voice_memo_only,
      two_sunday_test: c.two_sunday_test,
      process_configured: true,
    }).eq('id', church.id)
    if (error) return setErr(readableError(error))
    setSaved(true)
    await refresh()
  }

  const isPhone = device === 'phone'
  const suggestedSteps = steps
    ? SUGGESTED_STEPS.filter(([label]) => !steps.some((s) => s.label.toLowerCase() === label.toLowerCase()))
    : []
  if (!church || steps === null || !c) {
    return isPhone
      ? <Shell><Spinner /></Shell>
      : <PortalFrame title="Setup"><Spinner /></PortalFrame>
  }

  /* ---------- desktop / tablet ----------
     Steps on the left, the church's own defaults and range on the right.
     Saving is per-change on the steps (they persist on reorder already) and
     explicit for the church row, which is why the save button lives in the
     defaults card rather than at the bottom of the page. */
  if (!isPhone) {
    return (
      <PortalFrame title="Setup" sub="How your church reviews songs — steps, defaults, and range.">
        <div className="pf-cols pf-cols-2">
          <div className="pf-card">
            <div className="pf-cardhead"><span className="pf-cardtitle">Review steps</span></div>
            {steps.map((s, i) => (
              <div key={s.id} className="pf-step"
                   draggable={!s.is_fixed}
                   onDragStart={() => { dragFrom.current = i }}
                   onDragOver={(e) => e.preventDefault()}
                   onDrop={() => move(dragFrom.current, i)}>
                <span className="pf-step-n">{i + 1}</span>
                <span className="pf-step-b">
                  <span className="pf-step-t">{s.label}</span>
                  {s.description && <span className="pf-step-d">{s.description}</span>}
                </span>
                {s.is_fixed
                  ? <span className="pf-song-n" title="This step is fixed">Fixed</span>
                  : <span className="pf-rowbtns">
                      <button className="pf-btn pf-btn-quiet" onClick={() => move(i, i - 1)} aria-label="Move up">↑</button>
                      <button className="pf-btn pf-btn-quiet" onClick={() => move(i, i + 1)} aria-label="Move down">↓</button>
                      <button className="pf-btn pf-btn-quiet" onClick={() => removeStep(s)} aria-label="Remove step">×</button>
                    </span>}
              </div>
            ))}

            {draft ? (
              <div style={{ paddingTop: 6 }}>
                <Field label="What happens at this step?">
                  <input className="input" autoFocus value={draft.label}
                         onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                         placeholder="Band run-through" />
                </Field>
                <Field label="A line of detail (optional)">
                  <input className="input" value={draft.description}
                         onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                </Field>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="pf-btn pf-btn-solid" disabled={!draft.label.trim()}
                          onClick={() => addStep(draft.label, draft.description)}>Add step</button>
                  <button className="pf-btn pf-btn-quiet" onClick={() => setDraft(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button className="pf-btn" style={{ width: '100%', marginTop: 4 }}
                      onClick={() => setDraft({ label: '', description: '' })}>
                + Add step
              </button>
            )}

            {suggestedSteps.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <p className="small" style={{ marginBottom: 8 }}>
                  Suggestions — common steps other churches use. Deleting a step from your
                  process just sends it back here; nothing is lost.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {suggestedSteps.map(([label, description]) => (
                    <button key={label} className="chip" onClick={() => addStep(label, description)}>
                      + {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="pf-card" style={{ marginBottom: 14 }}>
              <div className="pf-cardhead"><span className="pf-cardtitle">Process defaults</span></div>
              {DEFAULTS.map(([k, title, hint]) => (
                <ToggleRow key={k} title={title} hint={hint}
                           on={c[k]} onChange={(v) => { setSaved(false); setC({ ...c, [k]: v }) }} />
              ))}
              {/* The phone flow answers a save with a full success screen. That
                  is the right shape for a first-run wizard and the wrong one
                  for a settings panel you edit repeatedly — so desktop
                  confirms in place and goes quiet again on the next edit. */}
              <button className="btn btn-primary" style={{ marginTop: 12 }}
                      disabled={saved} onClick={saveChurch}>
                {saved ? 'Saved' : 'Save our process'}
              </button>
            </div>

            <div className="pf-card">
              <div className="pf-cardhead"><span className="pf-cardtitle">Planning Center</span></div>
              {pcoNotice === 'error' && <p className="err">That didn't connect — try again.</p>}
              {pcoNotice === 'expired' && <p className="err">That link expired — try connecting again.</p>}
              {pcoNotice === 'cancelled' && <p className="pf-empty">Connection cancelled — nothing changed.</p>}
              {pcoConnected === null ? (
                <Spinner />
              ) : pcoConnected ? (
                <>
                  <p className="pf-empty" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <PcoIcon size={16} />
                    <span className="pf-pill pf-pill-ok">Active</span>{' '}
                    Endorsed songs can be pushed straight into your Services library from Endorsements.
                  </p>
                  <button className="pf-btn" disabled={pcoBusy} onClick={disconnectPlanningCenter}
                          style={{ marginTop: 8 }}>
                    {pcoBusy ? 'Working…' : 'Disconnect'}
                  </button>
                </>
              ) : (
                <>
                  <p className="pf-empty" style={{ marginBottom: 8 }}>
                    Connect your Planning Center account so endorsed songs can be pushed
                    straight into your Services library.
                  </p>
                  <button className="pf-btn pf-btn-solid" disabled={pcoBusy} onClick={connectPlanningCenter}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <PcoIcon size={15} />
                    {pcoBusy ? 'Connecting…' : 'Connect Planning Center'}
                  </button>
                </>
              )}
            </div>

            <div className="pf-card" style={{ marginTop: 14 }}>
              <div className="pf-cardhead"><span className="pf-cardtitle">At a glance</span></div>
              <div className="pf-stats">
                <div className="pf-stat">
                  <div className="pf-stat-n">{steps.length}</div>
                  <div className="pf-stat-l">Steps in your process</div>
                </div>
                <div className="pf-stat">
                  <div className="pf-stat-n">{DEFAULTS.filter(([k]) => c[k]).length}/{DEFAULTS.length}</div>
                  <div className="pf-stat-l">Defaults turned on</div>
                </div>
              </div>
            </div>

            <ChurchSize church={c} onSaved={(next) => { setC(next); refresh() }} />
            {err && <p className="err">{err}</p>}
          </div>
        </div>
      </PortalFrame>
    )
  }

  /* ---------- phone: unchanged ---------- */
  if (saved) return (
    <Shell back="/admin/people" title="People">
      <div className="done-ic"><Check size={26} w={2.4} /></div>
      <h2 className="q center">Your process is live.</h2>
      <p className="sub center">
        Writers at your church will be walked through submission, splits and co-sign automatically.
        Your reviewers only ever see finished, signed songs.
      </p>
      <div className="trust">
        <div className="trustrow"><span className="tick"><Check /></span>
          <span className="tt">{steps.length} step{steps.length === 1 ? '' : 's'} · senior sign-off {c.require_senior_signoff ? 'on' : 'off'}
            <small>Change any time in Admin</small></span></div>
      </div>
      <button className="btn btn-quiet" onClick={() => setSaved(false)}>Keep editing</button>
    </Shell>
  )

  return (
    <Shell back="/admin/people" title="People">
      <div className="eyebrow">Administrator</div>
      <h2 className="q">How does your church review songs?</h2>
      <p className="sub">Set the steps a song moves through. Drag to reorder — first and last are fixed.</p>
      <Sill />

      {steps.map((s, i) => (
        <div key={s.id}
             className={`step-item${s.is_fixed ? ' fixed' : ''}`}
             draggable={!s.is_fixed}
             onDragStart={() => { dragFrom.current = i }}
             onDragOver={(e) => e.preventDefault()}
             onDrop={() => move(dragFrom.current, i)}>
          <span className="grip">⠿</span>
          <span className="lbl">
            {s.label}
            {s.description && <small>{s.description}</small>}
          </span>
          {!s.is_fixed && <>
            <button className="x" title="Move up" onClick={() => move(i, i - 1)}>↑</button>
            <button className="x" title="Move down" onClick={() => move(i, i + 1)}>↓</button>
          </>}
          <button className="x" onClick={() => removeStep(s)}>×</button>
        </div>
      ))}

      {draft ? (
        <div style={{ padding: '8px 0 4px' }}>
          <Field label="What happens at this step?">
            <input className="input" autoFocus value={draft.label}
                   onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                   placeholder="Band run-through" />
          </Field>
          <Field label="A line of detail (optional)">
            <input className="input" value={draft.description}
                   onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" disabled={!draft.label.trim()}
                    onClick={() => addStep(draft.label, draft.description)}>
              Add step
            </button>
            <button className="btn btn-quiet" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn-plain" style={{ paddingLeft: 0, marginTop: 6 }}
                onClick={() => setDraft({ label: '', description: '' })}>
          + Add a step
        </button>
      )}

      {suggestedSteps.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p className="small" style={{ marginBottom: 8 }}>
            Suggestions — deleting a step just sends it back here, nothing is lost.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {suggestedSteps.map(([label, description]) => (
              <button key={label} className="chip" onClick={() => addStep(label, description)}>
                + {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Was a rule, then a 17px inline-styled h3, then a stack of loose
          toggles — three different section shapes on one screen. Same head and
          same group as everywhere else now. */}
      <h3 className="sechead">
        A few defaults
        <span className="note">Kept short — most churches never change these.</span>
      </h3>
      <div className="rows">
      <ToggleRow title="Require senior pastor sign-off" hint="Adds a second reviewer before Sunday"
        on={c.require_senior_signoff} onChange={(v) => setC({ ...c, require_senior_signoff: v })} />
      <ToggleRow title="Show writers the full status trail" hint="They see every step, or just the outcome"
        on={c.show_writers_full_trail} onChange={(v) => setC({ ...c, show_writers_full_trail: v })} />
      <ToggleRow title="Let writers submit voice memos only" hint="No lyrics required to start"
        on={c.allow_voice_memo_only} onChange={(v) => setC({ ...c, allow_voice_memo_only: v })} />
      <ToggleRow title="Two-Sunday test before rotation" hint="Does the room sing it"
        on={c.two_sunday_test} onChange={(v) => setC({ ...c, two_sunday_test: v })} />
      </div>

      <h3 className="sechead">Planning Center</h3>
      {pcoNotice === 'error' && <p className="err">That didn't connect — try again.</p>}
      {pcoNotice === 'expired' && <p className="err">That link expired — try connecting again.</p>}
      {pcoNotice === 'cancelled' && <p className="sub">Connection cancelled — nothing changed.</p>}
      {pcoConnected === null ? (
        <Spinner />
      ) : pcoConnected ? (
        <>
          <p className="sub" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PcoIcon size={16} />
            <span className="badge">Active</span> Endorsed songs can be pushed into your Services library.
          </p>
          <button className="btn btn-quiet" disabled={pcoBusy} onClick={disconnectPlanningCenter}>
            {pcoBusy ? 'Working…' : 'Disconnect'}
          </button>
        </>
      ) : (
        <>
          <p className="sub">Connect your Planning Center account to push endorsed songs into your Services library.</p>
          <button className="btn btn-quiet" disabled={pcoBusy} onClick={connectPlanningCenter}>
            <PcoIcon size={17} />
            {pcoBusy ? 'Connecting…' : 'Connect Planning Center'}
          </button>
        </>
      )}

      <div className="hr" style={{ marginTop: 30 }} />
      <button className="btn btn-primary" onClick={saveChurch}>Save our process</button>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
